import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { EmissionWithTitresDTO, PrimaireEmissionModel, ResultatAdjudicationDTO, TitresDTO } from '../models/emission.model';
import { IPagedResponse } from '../models/page.model';

@Injectable({
  providedIn: 'root'
})
export class EmissionService {
  private http = inject(HttpClient);
  private readonly baseUrl = 'income/v1/emission';
  private readonly resultatUrl = 'income/v1/resultat-adjudication';

  /**
   * Récupérer toutes les émissions avec leurs titres (paginé)
   */
  getAllEmissionsWithDetails(page: number = 0, size: number = 10, isPaged: boolean = true): Observable<IPagedResponse<EmissionWithTitresDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('isPaged', isPaged.toString());

    return this.http.get<IPagedResponse<EmissionWithTitresDTO>>(`${this.baseUrl}/with-details`, { params });
  }

  /**
   * Récupérer toutes les émissions
   */
  getAllEmissions(): Observable<EmissionWithTitresDTO[]> {
    return this.http.get<EmissionWithTitresDTO[]>(this.baseUrl);
  }

  /**
   * Récupérer tous les titres avec filtres optionnels
   */
  getAllTitres(page: number = 0, size: number = 10, isPaged: boolean = true, typeInstrument?: string, region?: string, startDate?: Date, endDate?: Date): Observable<IPagedResponse<TitresDTO>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('isPaged', isPaged.toString());

    if (typeInstrument) {
      params = params.set('typeInstrument', typeInstrument);
    }

    if (region) {
      params = params.set('region', region);
    }

    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }

    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }

    return this.http.get<IPagedResponse<TitresDTO>>(`${this.baseUrl}/details`, { params });
  }

  /**
   * Récupérer une émission par ID
   */
  getEmissionById(id: string): Observable<EmissionWithTitresDTO> {
    return this.http.get<EmissionWithTitresDTO>(`${this.baseUrl}/${id}`);
  }

  /**
   * Créer une nouvelle émission avec ses titres
   */
  createEmission(emission: PrimaireEmissionModel): Observable<EmissionWithTitresDTO> {
    return this.http.post<EmissionWithTitresDTO>(this.baseUrl, emission);
  }

  /**
   * Ajouter un titre à une émission existante
   */
  addTitreToEmission(emissionId: string, titre: TitresDTO): Observable<TitresDTO> {
    return this.http.post<TitresDTO>(`${this.baseUrl}/${emissionId}/titres`, titre);
  }

  /**
   * Importer des émissions depuis Excel
   */
  uploadEmissions(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.baseUrl}/upload`, formData);
  }

  /**
   * Supprimer un titre ou une émission
   * @param id - ID du titre ou de l'émission
   * @param isDetails - true pour supprimer un titre, false pour supprimer une émission complète
   */
  deleteEmissionOrTitre(id: string, isDetails: boolean = true): Observable<any> {
    const params = new HttpParams().set('isDetails', isDetails.toString());
    return this.http.delete(`${this.baseUrl}/${id}`, { params });
  }

  /**
   * Créer un résultat d'adjudication
   */
  createResultatAdjudication(resultat: ResultatAdjudicationDTO): Observable<ResultatAdjudicationDTO> {
    return this.http.post<ResultatAdjudicationDTO>(this.resultatUrl, resultat);
  }

  /**
   * Mettre à jour un résultat d'adjudication
   */
  updateResultatAdjudication(id: string, resultat: ResultatAdjudicationDTO): Observable<ResultatAdjudicationDTO> {
    return this.http.put<ResultatAdjudicationDTO>(`${this.resultatUrl}/${id}`, resultat);
  }

  /**
   * Récupérer un résultat d'adjudication par ID de titre
   */
  getResultatByTitreId(titreId: string): Observable<ResultatAdjudicationDTO> {
    return this.http.get<ResultatAdjudicationDTO>(`${this.resultatUrl}/titre/${titreId}`);
  }

  /**
   * Récupérer un résultat d'adjudication par ID
   */
  getResultatById(id: string): Observable<ResultatAdjudicationDTO> {
    return this.http.get<ResultatAdjudicationDTO>(`${this.resultatUrl}/${id}`);
  }

  /**
   * Supprimer un résultat d'adjudication
   */
  deleteResultatAdjudication(id: string): Observable<any> {
    return this.http.delete(`${this.resultatUrl}/${id}`);
  }
}
